<template>
    <div class="task-drawer">
        <confirm id="confirmDeleteTaskNote" ref="confirmDeleteTaskNote" @confirm="deleteNote">
            <template slot="title">
                <span>{{ t("tasks-confirm_delete_note_title") }}</span>
            </template>
            <template slot="body">
                <div>{{ t("tasks-confirm_delete_note_body") }}</div>
            </template>
        </confirm>
        <confirm id="confirmArchiveTask" ref="confirmArchiveTask" @confirm="archive(activeTask.id)">
            <template slot="title">
                <span>
                    {{
                        activeTask.archived == 1
                            ? t("tasks-confirm_archive_task_title_restore")
                            : t("tasks-confirm_archive_task_title_archive")
                    }}
                    <b>"{{ activeTask.title }}"</b>?
                </span>
            </template>
            <template slot="body">
                <div>
                    {{
                        activeTask.archived == 1
                            ? t("tasks-confirm_archive_task_body_restored")
                            : t("tasks-confirm_archive_task_body_archived")
                    }}
                </div>
            </template>
        </confirm>

        <div class="task-drawer-header">
            <div class="task-drawer-header-top">
                <div class="task-drawer-header-left">
                    <button type="button" class="task-back-btn" :aria-label="t('tasks-back')" @click="$emit('closeModal')">
                        <Icon icon-class="ui-arrow-left" size="5" />
                    </button>
                    <kv-chip v-if="activeTask.completed_at" size="small" density="extra-compact" rounded="pill" color="success">
                        {{ t("tasks-status_completed") }}
                    </kv-chip>
                    <kv-chip v-if="Number(activeTask.archived) === 1" size="small" density="extra-compact" rounded="pill" color="muted">
                        {{ t("tasks-status_archived") }}
                    </kv-chip>
                </div>
                <div class="task-drawer-actions">
                    <kv-button v-if="!activeTask.completed_at && Number(activeTask.archived) !== 1" variant="outlined" size="small" prepend-icon="ui-check" @click="complete(activeTask.id)">
                        {{ t("tasks-mark_as_completed") }}
                    </kv-button>
                    <kv-button variant="outlined" size="small" prepend-icon="ui-archive" @click="$refs.confirmArchiveTask.open()" />
                </div>
            </div>
            <div class="task-title-block">
                <input ref="titleInput" class="task-title-input" type="text" :value="title" :placeholder="t('tasks-title_placeholder')" @input="onTitleInput" />
                <benefits-rich-text class="task-description-editor" v-model="description" :placeholder="t('tasks-description_placeholder')" height="120px" variant="outlined" size="regular" @input="onDescriptionInput" />
            </div>
        </div>

        <div class="task-drawer-body">
            <div class="task-meta-list">
                <div class="meta-row task-tags-row">
                    <answer-tags with-label trigger="plus" :tags="activeTask.tags || []" :suggestions="tagSuggestions" :busy="tagBusy" @apply="applyTag" @remove="removeTag" @update="updateTagDefinition" @delete="deleteTagDefinition" />
                </div>
                <div class="meta-row task-client-row">
                    <span class="meta-row-label">
                        <Icon icon-class="ui-user-01" size="3" />
                        {{ t("tasks-client") }}
                    </span>
                    <div class="task-client-controls">
                        <clients-field class="task-client-field" name-only v-model="clients_id" />
                        <div class="task-client-actions">
                            <kv-tooltip v-if="client && client.whatsapp" :text="client.whatsapp">
                                <btn-whatsapp class="task-client-icon-btn" :number="client.whatsapp">
                                    <span class="task-client-icon is-whatsapp">
                                        <span class="fa fa-whatsapp"></span>
                                    </span>
                                </btn-whatsapp>
                            </kv-tooltip>
                            <kv-tooltip v-if="client && client.email" :text="client.email">
                                <btn-email class="task-client-icon-btn" :email="client.email">
                                    <span class="task-client-icon">
                                        <Icon icon-class="ui-mail-02" size="3" />
                                    </span>
                                </btn-email>
                            </kv-tooltip>
                            <kv-tooltip v-if="client && client.id" :text="t('tasks-view_profile')">
                                <router-link class="task-client-icon" :to="'/client/' + client.id">
                                    <Icon icon-class="ui-link-01" size="3" />
                                </router-link>
                            </kv-tooltip>
                        </div>
                    </div>
                </div>
                <div class="meta-row">
                    <span class="meta-row-label">
                        <Icon icon-class="ui-user-01" size="3" />
                        {{ t("tasks-assignee") }}
                    </span>
                    <kv-select class="meta-select" v-model="assignee_id" :options="assigneeOptions" value-field="value" label-field="label" size="small" variant="outlined" filterable :placeholder="t('tasks-none')" />
                </div>
                <div class="meta-row">
                    <span class="meta-row-label">
                        <Icon icon-class="ui-list" size="3" />
                        {{ t("tasks-col_board") }}
                    </span>
                    <kv-select class="meta-select" v-model="tasks_boards_id" :options="boardOptions" value-field="value" label-field="label" size="small" variant="outlined" filterable :placeholder="t('tasks-no_board_option')" />
                </div>
                <div class="meta-row">
                    <span class="meta-row-label">
                        <Icon icon-class="ui-check-done-02" size="3" />
                        {{ t("tasks-col_stage") }}
                    </span>
                    <kv-select class="meta-select" v-model="tasks_boards_columns_id" :options="stageOptions" value-field="value" label-field="label" size="small" variant="outlined" filterable :disabled="!tasks_boards_id" :placeholder="t('tasks-none')" />
                </div>
                <div class="meta-row">
                    <span class="meta-row-label">
                        <Icon icon-class="ui-calendar" size="3" />
                        {{ t("tasks-col_due_date") }}
                    </span>
                    <div class="meta-due">
                        <kv-date-picker :value="due_date" value-format="yyyy-MM-dd" size="small" variant="tonal" placement="bottom left" :placeholder="t('tasks-no_date')" :clearable="true" @input="onDueDateInput" />
                    </div>
                </div>
                <div class="meta-row">
                    <span class="meta-row-label">
                        <Icon icon-class="ui-flag-01" size="3" />
                        {{ t("tasks-priority") }}
                    </span>
                    <kv-popup placement="bottom left" min-width="170px">
                        <template #trigger="{ toggle }">
                            <button type="button" class="priority-trigger" :class="'tone-' + priorityKey" @click="toggle">
                                <Icon icon-class="ui-flag-01" size="3" />
                                <span>{{ priorityLabel }}</span>
                            </button>
                        </template>
                        <template #content="{ toggle }">
                            <div class="priority-menu">
                                <button v-for="opt in priorityOptions" :key="opt.value" type="button" class="priority-menu-item" :class="'tone-' + opt.tone" @click.stop.prevent="
                                    setPriority(opt.value, toggle)
                                    ">
                                    <Icon :icon-class="opt.value === 0
                                        ? 'ui-x-close'
                                        : 'ui-flag-01'
                                        " size="3" />
                                    {{ opt.label }}
                                </button>
                            </div>
                        </template>
                    </kv-popup>
                </div>
                <div v-if="automationLabel || createdByLabel" class="meta-row">
                    <span class="meta-row-label">
                        <Icon :icon-class="automationLabel
                            ? 'ui-refresh-ccw-01'
                            : 'ui-user-01'
                            " size="3" />
                        {{ t("tasks-created_by_label") }}
                    </span>
                    <router-link v-if="automationLabel && activeTask.automation && activeTask.automation.id" class="meta-created-by is-link" :to="'/automations/report/' + activeTask.automation.id">
                        {{ t("tasks-automation_source", { 0: automationLabel }) }}
                    </router-link>
                    <span v-else-if="automationLabel" class="meta-created-by">
                        {{ t("tasks-automation_source", { 0: automationLabel }) }}
                    </span>
                    <span v-else class="meta-created-by">
                        {{ createdByLabel }}
                    </span>
                </div>
            </div>

            <linked-answer-panel v-if="activeTask.answer && activeTask.answer.id" class="linked-answer-in-task" :answer="activeTask.answer" :default-open="true" :show-inbox-link="true" />

            <linked-commerce-panel v-if="activeTask.order && activeTask.order.id" class="linked-commerce-in-task" type="order" :item="activeTask.order" :default-open="true" />

            <linked-commerce-panel v-if="
                $module('quotations') &&
                activeTask.quotation &&
                activeTask.quotation.id
            " class="linked-commerce-in-task" type="quotation" :item="activeTask.quotation" :default-open="true" />

            <div v-if="activeTask.suggested_message" class="task-section">
                <h3>{{ t("tasks-suggested_message") }}</h3>
                <div class="alert alert-danger" v-if="
                    activeTask.client &&
                    activeTask.client.subscribed_whatsapp === '0'
                ">
                    {{ t("tasks-client_not_subscribed_whatsapp") }}
                </div>
                <div class="suggested-actions">
                    <btn-whatsapp v-if="activeTask.client && activeTask.client.whatsapp" :number="activeTask.client.whatsapp" :text="activeTask.suggested_message">
                        <button class="btn btn-success btn-small">
                            {{ t("tasks-send_via_whatsapp") }}
                        </button>
                    </btn-whatsapp>
                    <btn-email v-if="activeTask.client && activeTask.client.email" :email="activeTask.client.email" :text="activeTask.suggested_message">
                        <button class="btn btn-secondary btn-small">
                            {{ t("tasks-send_via_email") }}
                        </button>
                    </btn-email>
                </div>
                <div class="task-description-box" v-html="nl2br(activeTask.suggested_message)"></div>
            </div>

            <section class="task-section">
                <div class="section-header">
                    <h3>{{ t("tasks-attachments_title") }}</h3>
                    <label class="attachment-upload-btn">
                        <Icon icon-class="ui-upload-02" size="3" />
                        <span v-if="!uploadingFiles">{{
                            t("tasks-attachments_upload")
                        }}</span>
                        <span v-else>{{
                            t("tasks-attachments_uploading")
                        }}</span>
                        <input type="file" multiple hidden :disabled="uploadingFiles" @change="onFilesSelected" />
                    </label>
                </div>
                <ul v-if="taskFiles.length" class="attachments-list">
                    <li v-for="file in taskFiles" :key="file.id" class="attachment-item">
                        <a class="attachment-main" :href="file.url" target="_blank" rel="noopener noreferrer">
                            <span class="attachment-thumb">
                                <img v-if="isImageFile(file)" :src="file.url" :alt="file.name || ''" />
                                <Icon v-else :icon-class="fileIcon(file)" size="4" />
                            </span>
                            <span class="attachment-meta">
                                <span class="attachment-name">{{
                                    file.name || file.url
                                }}</span>
                                <span v-if="formatFileSize(file.size)" class="attachment-size">
                                    {{ formatFileSize(file.size) }}
                                </span>
                            </span>
                        </a>
                        <kv-popup placement="bottom left" min-width="140px">
                            <template #trigger="{ toggle }">
                                <button type="button" class="attachment-menu-btn" :aria-label="t('tasks-attachments_delete')" :disabled="uploadingFiles" @click.stop="toggle">
                                    <span class="mdi mdi-dots-vertical"></span>
                                </button>
                            </template>
                            <template #content="{ toggle }">
                                <button type="button" class="attachment-menu-item danger" @click.stop.prevent="
                                    removeFile(file);
                                toggle(false);
                                ">
                                    {{ t("tasks-attachments_delete") }}
                                </button>
                            </template>
                        </kv-popup>
                    </li>
                </ul>
                <p v-else class="empty-hint">
                    {{ t("tasks-attachments_empty") }}
                </p>
            </section>

            <section class="task-section">
                <h3>{{ t("tasks-notes_title") }}</h3>
                <div class="note-card" v-for="note in activeTask.notes" :key="note.id">
                    <div class="note-meta">
                        <strong>{{ note.user_name || note.name }}</strong>
                        <human-date v-if="note.created_at" :date="note.created_at" :format="t('dates-format_date_time')" />
                    </div>
                    <markdown-visualizer v-if="editingNoteId !== note.id" class="note-body" :text="note.note" :mention-items="mentionItems" />
                    <kv-markdown-editor v-else v-model="editingNoteText" :mention-items="activeMentionItems" />
                    <div class="note-actions" v-if="note.users_id == user.id">
                        <template v-if="editingNoteId !== note.id">
                            <button class="btn btn-link btn-small" @click="editNote(note)">
                                {{ t("tasks-edit_note") }}
                            </button>
                            <button class="btn btn-link btn-small" @click="confirmDeleteNote(note)">
                                {{ t("tasks-delete_note") }}
                            </button>
                        </template>
                        <template v-else>
                            <button class="btn btn-primary btn-small" :disabled="savingNote" @click="saveEditedNote">
                                {{ t("tasks-save") }}
                            </button>
                            <button class="btn btn-secondary btn-small" @click="cancelEditNote">
                                {{ t("tasks-cancel") }}
                            </button>
                        </template>
                    </div>
                </div>
                <kv-markdown-editor v-model="noteField" :mention-items="activeMentionItems" :placeholder="t('tasks-note_placeholder')" />
                <div class="note-composer-actions">
                    <button class="btn btn-primary btn-small" :disabled="savingNote || !noteField.trim()" @click.prevent="addNote">
                        {{ t("tasks-add_note") }}
                    </button>
                </div>
            </section>
        </div>
    </div>
</template>

<style scoped lang="scss">
.task-drawer {
    min-height: 100%;
    background: transparent;
    color: #111927;
}

.task-drawer-header {
    padding: 16px 24px 8px;
    border-bottom: 1px solid #f3f4f6;
}

.task-drawer-header-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
}

.task-drawer-header-left {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.task-back-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    margin: 0;
    border: none;
    background: transparent;
    color: #384250;
    cursor: pointer;
    line-height: 0;

    &:hover {
        color: #111927;
    }
}

.task-drawer-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-end;
}

.task-title-block {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.task-tags-row {
    align-items: flex-start;

    ::v-deep .answer-tags {
        width: 100%;
    }
}

.task-title-input {
    display: block;
    width: 100%;
    border: none;
    outline: none;
    resize: none;
    background: transparent;
    padding: 0;
    margin: 0;
    box-shadow: none;
    font-family: inherit;
    box-sizing: border-box;
    font-size: 24px;
    font-weight: 600;
    line-height: 1.2;
    height: 29px;
    color: #111927;

    &:focus {
        outline: none;
        box-shadow: none;
    }

    &::placeholder {
        color: #9da4ae;
    }
}

.task-description-editor {
    width: 100%;
    margin-top: 8px;

    ::v-deep .kv-editor {
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        overflow: hidden;
        background: #fff;
    }

    ::v-deep .ProseMirror,
    ::v-deep .tiptap {
        min-height: 96px;
        font-size: 14px;
        line-height: 1.35;
        color: #6c737f;
    }
}

.task-drawer-body {
    padding: 8px 24px 40px;
    background: transparent;
}

.task-meta-list {
    display: flex;
    flex-direction: column;
    margin-bottom: 28px;
}

.task-client-controls {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    min-width: 0;
    flex: 1;
}

.task-client-field {
    flex: 1;
    min-width: 0;
    max-width: 220px;

    ::v-deep .autocomplete-input {
        height: 32px;
        min-height: 32px;
        padding: 4px 8px;
        font-size: 13px;
        border-radius: 6px;
        background: transparent;
        border-color: transparent;
        text-align: right;

        &:hover,
        &:focus {
            background: #f9fafb;
            border-color: #e5e7eb;
        }
    }

    ::v-deep .autocomplete-result-list {
        text-align: left;
        z-index: 20;
    }
}

.task-client-actions {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
}

.task-client-icon-btn {
    display: inline-flex;
    line-height: 0;
    text-decoration: none;
}

.task-client-icon {
    width: 28px;
    height: 28px;
    border-radius: 999px;
    border: 1px solid #e5e7eb;
    background: #fff;
    color: #6c737f;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    transition: background-color 0.12s ease, color 0.12s ease, border-color 0.12s ease;

    &:hover {
        background: #edfcf3;
        border-color: #a6f4c5;
        color: var(--primary, #0b9055);
    }

    &.is-whatsapp {
        color: #2bb937;
        font-size: 15px;

        &:hover {
            background: #ecfdf3;
            border-color: #abefc6;
            color: #22932c;
        }
    }
}

::v-deep .task-client-actions .kv-tooltip-container {
    display: inline-flex;
    line-height: 0;
}

.meta-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 10px 0;
    border-bottom: 1px solid #e5e7eb;
}

.meta-row-label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #6c737f;
    flex-shrink: 0;
}

.meta-select {
    max-width: 220px;
    width: 100%;
    margin-left: auto;
}

.meta-due {
    max-width: 168px;
    width: auto;
    margin-left: auto;
    flex-shrink: 0;

    ::v-deep .kv-date-picker,
    ::v-deep .kv-popup-container {
        width: auto;
        max-width: 100%;
        display: block;
    }

    ::v-deep .kv-date-trigger,
    ::v-deep .kv-input {
        max-width: 168px;
    }
}

.meta-created-by {
    font-size: 13px;
    color: #384250;
    text-align: right;
    text-decoration: none;
    max-width: 260px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &.is-link:hover {
        color: var(--primary, #0b9055);
        text-decoration: underline;
    }
}

.priority-trigger {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: none;
    background: transparent;
    padding: 4px 8px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;

    &.tone-high {
        color: #f04438;
    }

    &.tone-medium {
        color: #dc6803;
    }

    &.tone-low {
        color: #384250;
    }

    &.tone-undefined {
        color: #9da4ae;
    }

    &:hover {
        background: #f9fafb;
    }
}

.priority-menu {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.priority-menu-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    border: none;
    background: transparent;
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 13px;
    text-align: left;
    cursor: pointer;

    &:hover {
        background: #f9fafb;
    }

    &.tone-high {
        color: #f04438;
    }

    &.tone-medium {
        color: #dc6803;
    }

    &.tone-low {
        color: #384250;
    }

    &.tone-undefined {
        color: #6c737f;
    }
}

.task-section {
    margin-top: 28px;

    h3 {
        margin: 0 0 10px;
        font-size: 15px;
        font-weight: 600;
        color: #111927;
    }
}

.section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;

    h3 {
        margin: 0;
    }
}

.task-description-box {
    background: transparent;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 16px 18px;
    font-size: 14px;
    line-height: 1.55;
    color: #384250;
    min-height: 72px;
    white-space: pre-wrap;

    &:empty::before {
        content: "—";
        color: #9da4ae;
    }
}

.linked-answer-in-task,
.linked-commerce-in-task {
    margin-top: 24px;

    ::v-deep .kv-card {
        background: #fff;
    }
}

.linked-commerce-in-task+.linked-commerce-in-task {
    margin-top: 12px;
}

.suggested-actions {
    display: flex;
    gap: 8px;
    margin-bottom: 10px;
}

.attachment-upload-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    padding: 6px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #fff;
    color: #384250;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    line-height: 1.2;

    &:hover {
        background: #f9fafb;
        border-color: #d2d6db;
    }
}

.attachments-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.attachment-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: #fff;
}

.attachment-main {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    flex: 1;
    text-decoration: none;
    color: inherit;
}

.attachment-thumb {
    width: 40px;
    height: 40px;
    border-radius: 999px;
    background: #f3f4f6;
    color: #6c737f;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
}

.attachment-meta {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.attachment-name {
    font-size: 14px;
    font-weight: 600;
    color: #111927;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.attachment-size {
    font-size: 12px;
    color: #9da4ae;
}

.attachment-menu-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    border-radius: 8px;
    color: #6c737f;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;

    &:hover:not(:disabled) {
        background: #f3f4f6;
        color: #111927;
    }

    &:disabled {
        opacity: 0.5;
        cursor: wait;
    }

    .mdi {
        font-size: 18px;
        line-height: 1;
    }
}

.attachment-menu-item {
    display: block;
    width: 100%;
    border: none;
    background: transparent;
    padding: 8px 10px;
    border-radius: 8px;
    text-align: left;
    font-size: 13px;
    cursor: pointer;
    color: #384250;

    &.danger {
        color: #f04438;
    }

    &:hover {
        background: #f9fafb;
    }
}

.empty-hint {
    color: #9da4ae;
    font-size: 13px;
    margin: 0;
}

.note-card {
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 12px;
    margin-bottom: 10px;
    background: transparent;
}

.note-meta {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    font-size: 12px;
    color: #6c737f;
    margin-bottom: 6px;
}

.note-body {
    font-size: 14px;
    color: #384250;
}

.note-actions,
.note-composer-actions {
    display: flex;
    gap: 8px;
    margin-top: 8px;
    justify-content: flex-end;
}

@media (max-width: 768px) {
    .meta-row {
        flex-direction: column;
        align-items: flex-start;
        gap: 6px;
    }

    .meta-select {
        max-width: 100%;
        width: 100%;
        text-align: left;
        padding-left: 0;
        margin-left: 0;
    }

    .task-client-controls {
        width: 100%;
        justify-content: flex-start;
    }

    .task-client-field {
        max-width: 100%;
        flex: 1 1 auto;

        ::v-deep .autocomplete-input {
            text-align: left;
            padding-left: 0;
        }
    }

    .meta-due {
        width: 100%;
        max-width: 100%;
        justify-content: flex-start;

        ::v-deep .kv-date-picker,
        ::v-deep .kv-date-trigger,
        ::v-deep .kv-input {
            text-align: left;
        }
    }

    .meta-created-by {
        width: 100%;
        text-align: left;
        justify-content: flex-start;
    }
}
</style>

<script>
import BtnEmail from "../components/btn-email/btn-email";
import BtnWhatsapp from "../components/btn-whatsapp/btn-whatsapp";
import HumanDate from "../components/human-date/human-date";
import MarkdownVisualizer from "../components/inputs/markdown-visualizer.vue";
import KvMarkdownEditor from "../components/inputs/kv-markdown-editor.vue";
import keviApi from "../keviApi";
import { mapState } from "vuex";
import ClientsField from "../clients/clients-field";
import Icon from "../components/icon/icon";
import Text from "../helpers/Text";
import LinkedAnswerPanel from "../components/answers/linked-answer-panel.vue";
import LinkedCommercePanel from "../components/commerce/linked-commerce-panel.vue";
import AnswerTags from "../inbox/answer-tags.vue";
import KvChip from "../components/ui/kv-chip.vue";
import KvButton from "../components/form/kv-button.vue";
import KvPopup from "../components/ui/kv-popup.vue";
import KvTooltip from "../components/ui/kv-tooltip.vue";
import KvDatePicker from "../components/inputs/kv-date-picker.vue";
import KvSelect from "../components/inputs/kv-select.vue";
import {
    taskPriorityChipColor,
    taskPriorityKey,
    taskStatusChipColor,
    taskStatusKey,
} from "../helpers/taskDisplay";
import { safeFileUrl } from "../helpers/answerDisplay";
import BenefitsRichText from "../benefits/shared/benefits-rich-text.vue";
export default {
    props: {
        activeTask: {
            type: Object,
            default: null,
        },
        activeTaskId: {
            type: String,
            default: null,
        },
    },
    components: {
        MarkdownVisualizer,
        KvMarkdownEditor,
        BtnEmail,
        BtnWhatsapp,
        HumanDate,
        ClientsField,
        Icon,
        LinkedAnswerPanel,
        LinkedCommercePanel,
        AnswerTags,
        KvChip,
        KvButton,
        "kv-popup": KvPopup,
        "kv-tooltip": KvTooltip,
        "kv-date-picker": KvDatePicker,
        "kv-select": KvSelect,
        "benefits-rich-text": BenefitsRichText,
    },
    data() {
        return {
            savingNote: false,
            showAside: false,
            noteField: "",
            editingNoteId: null,
            editingNoteText: "",
            noteToDelete: null,
            assignee_id: this.activeTask.assignee_id
                ? String(this.activeTask.assignee_id)
                : "",
            clients_id: this.activeTask.clients_id || "",
            client: this.activeTask.client || {},
            uploadingFiles: false,
            priority: this.activeTask.priority,
            tasks_boards_id: this.activeTask.tasks_boards_id
                ? String(this.activeTask.tasks_boards_id)
                : "",
            tasks_boards_columns_id: this.activeTask.tasks_boards_columns_id
                ? String(this.activeTask.tasks_boards_columns_id)
                : "",
            due_date: this.activeTask.due_date
                ? String(this.activeTask.due_date).slice(0, 10)
                : null,
            title: this.activeTask.title || "",
            description: this.activeTask.description || "",
            canAssignTo: [],
            boards: [],
            suppressingWatch: false,
            contentSaveTimeout: null,
            currentTaskId: this.activeTask.id || null,
            contentSaveSeq: 0,
            pendingContentSaves: 0,
            placeTags: [],
            tagBusy: false,
        };
    },
    mounted() {
        let level = this.$store.state.user.permissions.tasks.options.level;
        this.$store.dispatch("placeDetails/setPlace", this.placeId).then(() => {
            let users = this.users;
            let me = users.find((u) => u.username === this.user.username);
            if (level === "mine") {
                this.canAssignTo = [{ ...me }];
            } else if (level === "branch") {
                this.canAssignTo = JSON.parse(
                    JSON.stringify(
                        users.filter(
                            (u) =>
                                u.places_branches_id ===
                                me.places_branches_id &&
                                parseInt(u.active) === 1,
                        ),
                    ),
                );
            } else {
                this.canAssignTo = JSON.parse(
                    JSON.stringify(
                        users.filter((u) => parseInt(u.active) === 1),
                    ),
                );
            }
        });
        this.loadBoards();
        this.loadPlaceTags();
    },
    beforeDestroy() {
        if (this.contentSaveTimeout) {
            clearTimeout(this.contentSaveTimeout);
        }
    },
    computed: {
        ...mapState({
            user: (state) => state.user,
        }),
        ...mapState("placeDetails", ["users", "roles", "branches"]),
        placeId() {
            return this.$store.state.place.id;
        },
        taskFiles() {
            // Urls are stored values rendered into href/src, so the scheme is
            // checked here too rather than trusting whatever is in the row.
            return (this.activeTask.files || [])
                .map((file) => ({ ...file, url: safeFileUrl(file.url) }))
                .filter((file) => file.url);
        },
        statusKey() {
            return taskStatusKey(this.activeTask);
        },
        statusLabel() {
            return this.t("tasks-status_" + this.statusKey);
        },
        statusColor() {
            return taskStatusChipColor(this.statusKey);
        },
        priorityKey() {
            return taskPriorityKey(this.priority);
        },
        priorityLabel() {
            const key = this.priorityKey;
            if (key === "medium") return this.t("tasks-priority_medium");
            if (key === "high") return this.t("tasks-priority_high");
            if (key === "low") return this.t("tasks-priority_low");
            return this.t("tasks-priority_undefined");
        },
        priorityColor() {
            return taskPriorityChipColor(this.priorityKey);
        },
        boardLabel() {
            return (
                this.activeTask.board_name ||
                (this.boards.find((b) => String(b.id) === String(this.tasks_boards_id)) || {})
                    .name ||
                ""
            );
        },
        boardColumns() {
            const board = this.boards.find(
                (b) => String(b.id) === String(this.tasks_boards_id),
            );
            const columns = (board && board.columns) || [];
            return columns
                .slice()
                .sort((a, b) => Number(a.position || 0) - Number(b.position || 0));
        },
        assigneeOptions() {
            return [
                { value: "", label: this.t("tasks-none") },
                ...(this.canAssignTo || []).map((user) => ({
                    value: String(user.id),
                    label: user.name,
                })),
            ];
        },
        boardOptions() {
            return [
                { value: "", label: this.t("tasks-no_board_option") },
                ...(this.boards || []).map((board) => ({
                    value: String(board.id),
                    label: board.name,
                })),
            ];
        },
        mentionItems() {
            return (this.users || []).map((u) => ({
                id: u.id,
                title: u.name,
                subtitle: u.role_name || u.email || '',
                image: u.photo || u.imagePicture || null
            }));
        },
        activeMentionItems() {
            return (this.users || [])
                .filter((u) => parseInt(u.active) === 1)
                .map((u) => ({
                    id: u.id,
                    title: u.name,
                    subtitle: u.role_name || u.email || '',
                    image: u.photo || u.imagePicture || null
                }));
        },
        stageOptions() {
            return [
                { value: "", label: this.t("tasks-none") },
                ...this.boardColumns.map((column) => ({
                    value: String(column.id),
                    label: column.name,
                })),
            ];
        },
        priorityOptions() {
            return [
                {
                    value: 1,
                    tone: "low",
                    label: this.t("tasks-priority_low"),
                },
                {
                    value: 2,
                    tone: "medium",
                    label: this.t("tasks-priority_medium"),
                },
                {
                    value: 3,
                    tone: "high",
                    label: this.t("tasks-priority_high"),
                },
                {
                    value: 0,
                    tone: "undefined",
                    label: this.t("tasks-priority_undefined"),
                },
            ];
        },
        createdByLabel() {
            return this.activeTask.created_by_name || "";
        },
        automationLabel() {
            const automation = this.activeTask && this.activeTask.automation;
            if (!automation) {
                return "";
            }
            if (typeof automation === "string") {
                return automation;
            }
            return automation.title || "";
        },
        tagSuggestions() {
            const available = this.activeTask.available_tags || [];
            if (available.length) {
                return available;
            }
            return this.placeTags;
        },
    },

    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        onTitleInput(event) {
            this.title = event.target.value;
            this.scheduleContentSave();
        },
        onDescriptionInput() {
            this.scheduleContentSave();
        },
        scheduleContentSave() {
            if (this.suppressingWatch) {
                return;
            }
            if (this.contentSaveTimeout) {
                clearTimeout(this.contentSaveTimeout);
            }
            this.contentSaveTimeout = setTimeout(() => {
                this.contentSaveTimeout = null;
                this.updateTask();
            }, 500);
        },
        loadBoards() {
            let options = keviApi.getOptions("/task-boards-columns", "GET");
            this.$http(options).then((response) => {
                this.boards = response.body || response.data || [];
            });
        },
        async loadPlaceTags() {
            try {
                const response = await this.$http(
                    keviApi.getOptions("tasks-tags"),
                );
                this.placeTags = (response.body && response.body.tags) || [];
            } catch (error) {
                console.error("Error loading task tags", error);
            }
        },
        rememberCreatedTag(tag) {
            if (!tag || !tag.id) return;
            const exists = this.placeTags.some(
                (item) => String(item.id) === String(tag.id),
            );
            if (!exists) {
                this.placeTags = this.placeTags.concat([tag]);
            }
        },
        applyTag(payload) {
            if (!this.activeTask || !this.activeTask.id) return;
            this.tagBusy = true;
            const options = keviApi.getOptions(
                "task/" + this.activeTask.id + "/tags",
                "POST",
                payload,
            );
            this.$http(options)
                .then((response) => {
                    const body = response.body || {};
                    const patch = { tags: body.tags || [] };
                    if (body.tag) {
                        this.rememberCreatedTag(body.tag);
                        const available = this.activeTask.available_tags || [];
                        const availableExists = available.some(
                            (item) => String(item.id) === String(body.tag.id),
                        );
                        if (!availableExists) {
                            patch.available_tags = available.concat([body.tag]);
                        }
                    }
                    this.$emit("changed", {
                        id: this.activeTask.id,
                        task: patch,
                        moveNext: false,
                    });
                })
                .catch(() => {
                    this.$store.dispatch(
                        "notification/error",
                        this.t("tasks-notify_tag_error"),
                    );
                })
                .finally(() => {
                    this.tagBusy = false;
                });
        },
        removeTag(tag) {
            if (!this.activeTask || !this.activeTask.id || !tag) return;
            this.tagBusy = true;
            const options = keviApi.getOptions(
                "task/" + this.activeTask.id + "/tags/" + tag.id,
                "DELETE",
            );
            this.$http(options)
                .then((response) => {
                    this.$emit("changed", {
                        id: this.activeTask.id,
                        task: {
                            tags: (response.body && response.body.tags) || [],
                        },
                        moveNext: false,
                    });
                })
                .catch(() => {
                    this.$store.dispatch(
                        "notification/error",
                        this.t("tasks-notify_tag_error"),
                    );
                })
                .finally(() => {
                    this.tagBusy = false;
                });
        },
        patchLocalTagDefinition(tag) {
            if (!tag || !tag.id) return;
            const patchList = (list) =>
                (list || []).map((item) =>
                    String(item.id) === String(tag.id)
                        ? { ...item, ...tag }
                        : item,
                );
            this.placeTags = patchList(this.placeTags);
            const available = this.activeTask.available_tags || [];
            const applied = this.activeTask.tags || [];
            this.$emit("changed", {
                id: this.activeTask.id,
                task: {
                    tags: patchList(applied),
                    available_tags: patchList(available),
                },
                moveNext: false,
            });
        },
        removeLocalTagDefinition(tagId) {
            const filterList = (list) =>
                (list || []).filter(
                    (item) => String(item.id) !== String(tagId),
                );
            this.placeTags = filterList(this.placeTags);
            this.$emit("changed", {
                id: this.activeTask.id,
                task: {
                    tags: filterList(this.activeTask.tags || []),
                    available_tags: filterList(
                        this.activeTask.available_tags || [],
                    ),
                },
                moveNext: false,
            });
        },
        updateTagDefinition(payload) {
            if (!payload || !payload.id) return;
            this.tagBusy = true;
            const options = keviApi.getOptions("tasks-tags", "POST", {
                id: payload.id,
                name: payload.name,
                color: payload.color,
            });
            this.$http(options)
                .then((response) => {
                    const tag = (response.body && response.body.tag) || payload;
                    this.patchLocalTagDefinition(tag);
                })
                .catch(() => {
                    this.$store.dispatch(
                        "notification/error",
                        this.t("tasks-notify_tag_error"),
                    );
                })
                .finally(() => {
                    this.tagBusy = false;
                });
        },
        deleteTagDefinition(tag) {
            if (!tag || !tag.id) return;
            this.tagBusy = true;
            const options = keviApi.getOptions(
                "tasks-tags/" + tag.id,
                "DELETE",
            );
            this.$http(options)
                .then(() => {
                    this.removeLocalTagDefinition(tag.id);
                })
                .catch(() => {
                    this.$store.dispatch(
                        "notification/error",
                        this.t("tasks-notify_tag_error"),
                    );
                })
                .finally(() => {
                    this.tagBusy = false;
                });
        },
        onDueDateInput(value) {
            const next =
                value == null || value === ""
                    ? null
                    : String(value).slice(0, 10);
            if ((this.due_date || null) === next) {
                return;
            }
            this.due_date = next;
        },
        setPriority(value, toggle) {
            if (typeof toggle === "function") {
                toggle(false);
            }
            this.priority = value;
        },
        async moveToColumn(columnId) {
            if (!this.tasks_boards_id || !columnId) {
                return;
            }
            try {
                const options = keviApi.getOptions(
                    `/task-boards/${this.tasks_boards_id}/tasks/${this.activeTask.id}/move`,
                    "POST",
                    {
                        tasks_boards_columns_id: columnId,
                        position: 0,
                    },
                );
                await this.$http(options);
                const column = this.boardColumns.find(
                    (c) => String(c.id) === String(columnId),
                );
                this.$emit("changed", {
                    id: this.activeTask.id,
                    task: {
                        tasks_boards_columns_id: columnId,
                        column_name: column ? column.name : null,
                    },
                    moveNext: false,
                });
            } catch (e) {
                this.$store.dispatch(
                    "notification/error",
                    this.t("tasks-notify_error_update"),
                );
            }
        },
        clearClient() {
            this.form.clients_id = null;
        },
        isImageFile(file) {
            const type = String((file && file.type) || "").toLowerCase();
            if (type.startsWith("image/")) {
                return true;
            }
            const name = String((file && file.name) || file.url || "").toLowerCase();
            return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name);
        },
        fileIcon(file) {
            const type = String((file && file.type) || "").toLowerCase();
            const name = String((file && file.name) || file.url || "").toLowerCase();
            if (type.startsWith("audio/") || /\.(mp3|wav|ogg|m4a)$/.test(name)) {
                return "ui-volume-max";
            }
            if (type.startsWith("video/") || /\.(mp4|mov|webm|avi)$/.test(name)) {
                return "ui-file-05";
            }
            return "ui-file-02";
        },
        formatFileSize(size) {
            const bytes = Number(size);
            if (!Number.isFinite(bytes) || bytes < 0) {
                return "";
            }
            if (bytes < 1024) {
                return `${bytes} B`;
            }
            if (bytes < 1024 * 1024) {
                return `${(bytes / 1024).toFixed(1)} KB`;
            }
            return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
        },
        onFilesSelected(event) {
            const files = Array.from(event.target.files || []);
            event.target.value = "";
            if (!files.length) return;
            this.uploadingFiles = true;
            const formData = new FormData();
            files.forEach((file) => formData.append("files[]", file));
            const options = keviApi.getOptions(
                `task/${this.activeTask.id}/files`,
                "POST",
                formData,
            );
            this.$http(options)
                .then((response) => {
                    const uploaded =
                        (response.body && response.body.files) || [];
                    const nextFiles = [
                        ...(this.activeTask.files || []),
                        ...uploaded,
                    ];
                    this.$emit("changed", {
                        id: this.activeTask.id,
                        task: { files: nextFiles },
                        moveNext: false,
                    });
                    this.$store.dispatch(
                        "notification/success",
                        this.t("tasks-notify_file_uploaded"),
                    );
                })
                .catch(() => {
                    this.$store.dispatch(
                        "notification/error",
                        this.t("tasks-notify_file_upload_error"),
                    );
                })
                .finally(() => {
                    this.uploadingFiles = false;
                });
        },
        removeFile(file) {
            if (!file || !file.id) {
                this.$store.dispatch(
                    "notification/error",
                    this.t("tasks-notify_file_delete_error"),
                );
                return;
            }
            this.uploadingFiles = true;
            const options = keviApi.getOptions(
                `task/${this.activeTask.id}/files/${file.id}`,
                "DELETE",
            );
            this.$http(options)
                .then(() => {
                    const nextFiles = (this.activeTask.files || []).filter(
                        (item) => String(item.id) !== String(file.id),
                    );
                    this.$set(this.activeTask, "files", nextFiles);
                    this.$emit("changed", {
                        id: this.activeTask.id,
                        task: { files: nextFiles },
                        moveNext: false,
                    });
                    this.$store.dispatch(
                        "notification/success",
                        this.t("tasks-notify_file_deleted"),
                    );
                })
                .catch(() => {
                    this.$store.dispatch(
                        "notification/error",
                        this.t("tasks-notify_file_delete_error"),
                    );
                })
                .finally(() => {
                    this.uploadingFiles = false;
                });
        },
        addNote() {
            this.savingNote = true;
            let options = keviApi.getOptions(
                "task/" + this.activeTask.id + "/notes",
                "PUT",
                {
                    note: this.noteField,
                },
            );
            return this.$http(options).then((response) => {
                this.savingNote = false;
                this.noteField = "";
                this.activeTask.notes.push(response.body);
                this.$store.dispatch(
                    "notification/success",
                    this.t('tasks-notify_note_saved'),
                );
            });
        },
        editNote(note) {
            this.editingNoteId = note.id;
            this.editingNoteText = this.decodeHtmlEntities(note.note);
        },
        decodeHtmlEntities(text) {
            const textArea = document.createElement("textarea");
            textArea.innerHTML = text;
            return textArea.value;
        },
        saveEditedNote() {
            if (!this.editingNoteText.trim()) {
                this.$store.dispatch(
                    "notification/error",
                    this.t('tasks-notify_note_empty'),
                );
                return;
            }

            this.savingNote = true;
            let options = keviApi.getOptions(
                "task/" + this.activeTask.id + "/updateNote/" + this.editingNoteId,
                "PUT",
                {
                    note: this.editingNoteText,
                },
            );
            this.$http(options).then((response) => {
                const noteIndex = this.activeTask.notes.findIndex(n => n.id == this.editingNoteId);
                if (noteIndex !== -1) {
                    const updatedNotes = [...this.activeTask.notes];
                    updatedNotes[noteIndex] = response.body;
                    this.$emit("changed", {
                        id: this.activeTask.id,
                        task: { notes: updatedNotes }
                    });
                }
                this.cancelEditNote();
                this.savingNote = false;
                this.$store.dispatch(
                    "notification/success",
                    this.t('tasks-notify_note_updated'),
                );
            }).catch(() => {
                this.savingNote = false;
                this.$store.dispatch(
                    "notification/error",
                    this.t('tasks-notify_error_update_note'),
                );
            });
        },
        cancelEditNote() {
            this.editingNoteId = null;
            this.editingNoteText = "";
        },
        confirmDeleteNote(note) {
            this.noteToDelete = note;
            this.$refs.confirmDeleteTaskNote.open();
        },
        deleteNote() {
            if (!this.noteToDelete) {
                return;
            }

            this.savingNote = true;
            let options = keviApi.getOptions(
                "task/" + this.activeTask.id + "/deleteNote/" + this.noteToDelete.id,
                "DELETE"
            );
            this.$http(options).then(() => {
                const updatedNotes = this.activeTask.notes.filter(n => n.id != this.noteToDelete.id);
                this.$emit("changed", {
                    id: this.activeTask.id,
                    task: { notes: updatedNotes }
                });
                this.noteToDelete = null;
                this.savingNote = false;
                this.$store.dispatch(
                    "notification/success",
                    this.t('tasks-notify_note_deleted'),
                );
            }).catch(() => {
                this.savingNote = false;
                this.$store.dispatch(
                    "notification/error",
                    this.t('tasks-notify_error_delete_note'),
                );
            });
        },
        complete(id) {
            const options = keviApi.getOptions(`task/${id}/complete`, "POST");
            this.$http(options).then((response) => {
                window.mixpanel?.track("Task completed", this.activeTask);
                let task = response.body.task;

                this.$emit("changed", {
                    id: task.id,
                    task: task,
                    moveNext: true,
                });
                this.$store.dispatch("notification/success", {
                    text: this.t("tasks-notify_task_completed"),
                    actionLabel: this.t("undo"),
                    onAction: () => this.uncomplete(id),
                });
            });
        },
        uncomplete(id) {
            const options = keviApi.getOptions(`task/${id}/uncomplete`, "POST");
            this.$http(options)
                .then((response) => {
                    const task = response.body.task || {};
                    this.$store.dispatch(
                        "notification/success",
                        this.t("tasks-notify_task_reopened"),
                    );
                    this.$emit("changed", {
                        id: id,
                        task: {
                            completed_at: task.completed_at || null,
                            ...task,
                        },
                        moveNext: false,
                    });
                })
                .catch(() => {
                    this.$store.dispatch(
                        "notification/error",
                        this.t("tasks-notify_error_update"),
                    );
                });
        },
        archive(id) {
            let task = this.activeTask;
            if (task) {
                const options = keviApi.getOptions(
                    `task/${id}/archive`,
                    "POST",
                    {
                        archived: task.archived == 0 ? true : false,
                    },
                );
                this.$http(options).then((response) => {
                    let task = response.body.task;
                    this.$store.dispatch(
                        "notification/success",
                        task.archived
                            ? this.t('tasks-notify_task_archived')
                            : this.t('tasks-notify_task_unarchived'),
                    );
                    window.mixpanel?.track(
                        task.archived ? "Task archived" : "Task unarchived",
                        task,
                    );
                    this.$emit("changed", {
                        id: task.id,
                        task: task,
                        moveNext: !!task.archived,
                    });
                });
            }
        },
        nl2br(str) {
            return Text.nl2br(str);
        },
        //Generic method for update one information at a time as they are set
        updateTask() {
            if (this.suppressingWatch) {
                return;
            }
            let task = this.activeTask;
            if (task) {
                const title =
                    String(this.title || "").trim() || task.title || "";
                if (!title) {
                    return;
                }
                const options = keviApi.getOptions(
                    `task/${task.id}/edit`,
                    "POST",
                    {
                        title,
                        description: this.description || "",
                        assignee_id: this.assignee_id || "",
                        clients_id: this.clients_id || "",
                        priority: this.priority,
                        tasks_boards_id: this.tasks_boards_id || "",
                        due_date: this.due_date || "",
                    },
                );
                const seq = ++this.contentSaveSeq;
                this.pendingContentSaves++;
                this.$http(options).then((response) => {
                    this.pendingContentSaves--;
                    window.mixpanel?.track("Task updated", task);
                    let updated = { ...response.body.task };
                    if (seq !== this.contentSaveSeq) {
                        delete updated.title;
                        delete updated.description;
                    }
                    const board = this.boards.find(
                        (b) => String(b.id) === String(updated.tasks_boards_id),
                    );
                    const column = ((board && board.columns) || []).find(
                        (c) =>
                            String(c.id) ===
                            String(updated.tasks_boards_columns_id),
                    );
                    this.$emit("changed", {
                        id: updated.id,
                        task: {
                            ...updated,
                            board_name: board ? board.name : null,
                            column_name: column
                                ? column.name
                                : updated.column_name || null,
                            assignee_name:
                                updated.assignee_name ||
                                (
                                    this.canAssignTo.find(
                                        (u) =>
                                            String(u.id) ===
                                            String(updated.assignee_id),
                                    ) || {}
                                ).name ||
                                null,
                        },
                        moveNext: false,
                    });
                }, () => {
                    this.pendingContentSaves--;
                });
            }
        },
    },
    watch: {
        activeTask: {
            handler(val) {
                this.suppressingWatch = true;
                
                const isSameTask = this.currentTaskId === val.id;
                this.currentTaskId = val.id;

                if (!isSameTask) {
                    if (this.contentSaveTimeout) {
                        clearTimeout(this.contentSaveTimeout);
                        this.contentSaveTimeout = null;
                    }
                    this.title = val.title || "";
                    this.description = val.description || "";
                } else {
                    const isEditingContent =
                        this.contentSaveTimeout || this.pendingContentSaves > 0;
                    if (!isEditingContent) {
                        this.title = val.title || "";
                        this.description = val.description || "";
                    }
                }

                this.assignee_id = val.assignee_id ? String(val.assignee_id) : "";
                this.clients_id = val.clients_id || "";
                this.client = val.client || {};
                this.priority = val.priority;
                this.due_date = val.due_date
                    ? String(val.due_date).slice(0, 10)
                    : null;
                this.tasks_boards_id = val.tasks_boards_id
                    ? String(val.tasks_boards_id)
                    : "";
                this.tasks_boards_columns_id = val.tasks_boards_columns_id
                    ? String(val.tasks_boards_columns_id)
                    : "";
                this.$nextTick(() => {
                    this.suppressingWatch = false;
                });
            },
            deep: true,
        },
        assignee_id() {
            this.updateTask();
        },
        tasks_boards_id(newId, oldId) {
            if (String(newId || "") === String(oldId || "")) {
                return;
            }
            // Board change resets to first column via API edit
            this.updateTask();
        },
        tasks_boards_columns_id(newId, oldId) {
            if (this.suppressingWatch) {
                return;
            }
            if (String(newId || "") === String(oldId || "")) {
                return;
            }
            if (!newId || !this.tasks_boards_id) {
                return;
            }
            // Same board stage move — use move endpoint
            if (
                String(this.activeTask.tasks_boards_id) ===
                String(this.tasks_boards_id)
            ) {
                this.moveToColumn(newId);
            }
        },
        clients_id: {
            handler(newId, oldId) {
                newId = newId ?? "";
                oldId = oldId ?? "";
                if (newId === oldId) {
                    return;
                }
                if (newId) {
                    const options = keviApi.getOptions(
                        "place/client/" + newId,
                        "GET",
                    );
                    this.$http(options).then((response) => {
                        this.client = response.body || {};
                    });
                } else {
                    this.client = {};
                }
                this.updateTask();
            },
        },
        priority() {
            this.updateTask();
        },
        due_date() {
            this.updateTask();
        },
    },
};
</script>
